import { useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";

import { WORKSPACE_BILLING_CHANGEABLE_STATUSES } from "@runcheetah/shared";

import { AssetCreationUpgradeModal } from "@/components/billing/asset-creation-upgrade-modal";
import { getAssetCreationBillingGateState } from "@/lib/asset-creation-billing-gate";
import { trpc } from "@/lib/trpc";

export function useAssetCreationGate(brandId: string | undefined) {
  const intl = useIntl();
  const location = useLocation();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const returnPath = `${location.pathname}${location.search}${location.hash}`;
  const billingAvailabilityQuery = trpc.brand.getBillingAvailability.useQuery(
    { brandId: brandId ?? "" },
    { enabled: Boolean(brandId), staleTime: 60_000 },
  );
  const billingOverviewQuery = trpc.brand.getBillingOverview.useQuery(
    { brandId: brandId ?? "" },
    {
      enabled: Boolean(brandId && billingAvailabilityQuery.data?.enabled),
      refetchOnWindowFocus: true,
      staleTime: 30_000,
    },
  );
  const billingPlansQuery = trpc.billing.listPlans.useQuery(undefined, {
    enabled: Boolean(brandId && billingAvailabilityQuery.data?.enabled && upgradeOpen),
    staleTime: 60_000,
  });
  const creditPacksQuery = trpc.billing.listCreditPacks.useQuery(undefined, {
    enabled: Boolean(brandId && billingAvailabilityQuery.data?.enabled && upgradeOpen),
    staleTime: 60_000,
  });
  const gateState = getAssetCreationBillingGateState({
    availability: billingAvailabilityQuery.data,
    overview: billingOverviewQuery.data,
    availabilityPending: billingAvailabilityQuery.isPending,
    overviewPending: Boolean(
      billingAvailabilityQuery.data?.enabled && billingOverviewQuery.isPending,
    ),
  });
  const account = billingOverviewQuery.data?.account ?? null;
  const upgradeLabel = intl.formatMessage(
    account?.hasStripeSubscription && WORKSPACE_BILLING_CHANGEABLE_STATUSES.includes(account.status)
      ? {
          id: "brandSettings.billing.buyCredits",
          defaultMessage: "Buy extra runs",
        }
      : {
          id: "runConfirmation.upgrade",
          defaultMessage: "Upgrade",
        },
  );

  const requestAssetCreation = (onAllowed: () => void) => {
    if (gateState === "allowed") {
      onAllowed();
      return;
    }

    if (gateState === "upgrade_required") {
      setUpgradeOpen(true);
    }
  };

  const upgradeModal = (
    <AssetCreationUpgradeModal
      brandId={brandId}
      overview={billingOverviewQuery.data}
      plans={billingPlansQuery.data ?? []}
      plansPending={billingPlansQuery.isPending || billingPlansQuery.isFetching}
      creditPacks={creditPacksQuery.data ?? []}
      creditPacksPending={creditPacksQuery.isPending || creditPacksQuery.isFetching}
      returnPath={returnPath}
      open={upgradeOpen}
      onOpenChange={setUpgradeOpen}
    />
  );

  return {
    gateState,
    requestAssetCreation,
    openUpgradeModal: () => setUpgradeOpen(true),
    upgradeLabel,
    upgradeModal,
  };
}
